import { createFileRoute } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { createTablePage } from '~/components/ui/createTablePage';
import { LIST_PAGE_SIZE } from '~/components/ui/Pagination';
import { LANGUAGE_LABELS } from '~/constants/languages';
import { CreateSeriesButton } from './CreateSeriesButton';

type SeriesSummary = components['schemas']['SeriesSummary'];

const columnHelper = createColumnHelper<SeriesSummary>();

const columns = [
  columnHelper.accessor('id', { header: 'ID' }),
  columnHelper.accessor('title', { header: 'Title' }),
  columnHelper.accessor('language', {
    header: 'Language',
    cell: (c) => LANGUAGE_LABELS[c.getValue()],
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (c) => (
      <StatusBadge $published={c.getValue() === 'published'}>
        {c.getValue()}
      </StatusBadge>
    ),
  }),
];

// Rows open the series detail page, where editing and the certificate live —
// matching courses, lessons and topics. A certificate is not a list-level
// concern: most series have one, so a column of it carries almost no signal.
const SeriesList = createTablePage<SeriesSummary, '/series/$seriesId'>({
  name: 'Series',
  useData: (page: number) =>
    api.adminListSeries.useQuery({
      query: { limit: LIST_PAGE_SIZE, offset: LIST_PAGE_SIZE * page },
    }),
  columns,
  itemBaseUrl: '/series/$seriesId',
  getParams: (row) => ({ seriesId: String(row.id) }),
  actions: <CreateSeriesButton />,
});

export const Route = createFileRoute('/_admin/series/')({
  component: SeriesList,
});

const StatusBadge = styled.span<{ $published?: boolean }>`
  background: ${(p) => (p.$published ? 'var(--success)' : 'var(--grey-200)')};
  border-radius: 999px;
  color: ${(p) => (p.$published ? 'var(--grey-50)' : 'var(--grey-700)')};
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  text-transform: capitalize;
`;
